import React from 'react';
import {Control, Controller} from 'react-hook-form';
import {FormattedMessage} from 'react-intl';
import EssentialImageFormItem from '~/features/base/components/forms/EssentialImageFormItem';
import DistroFormItem from '~/features/base/components/forms/DistroFormItem';
import BaseSwFormItem from '~/features/base/components/forms/BaseSwFormItem';
import InstalledDeliverablesFormGroup from '~/features/groups/components/InstalledDeliverablesFormGroup';
import {FILTER_IDS} from '~/features/devices/constants/filterMenuConstants';
import HeartWrapper from '~/features/devices/components/deviceFilters/HeartWrapper';
import MultiSelectOTA from '~/features/base/components/MultiSelectOTA';
import RadioButtonListOTA from "~/features/base/components/forms/ota/RadioButtonListOTA";
import {
    useFetchVehicleEnergyTypesQuery,
    useFetchMmtHeadUnitsQuery,
    useFetchFunctionKeysQuery,
} from '~/api/mmt/MmtManagement.api';

const mmtPropertiesOptions = [
    { id: 'all', label: 'All', value: 'all' },
    { id: 'true', label: 'Has MMT Properties', value: true },
    { id: 'false', label: 'No MMT Properties', value: false }
];

interface SoftwareFilterSectionProps {
    control: Control<any>;
    setValue: (name: string, value: any) => void;
}

const SoftwareFilterSection: React.FC<SoftwareFilterSectionProps> = ({ control, setValue }) => {
    const { data: energyTypes = [] } = useFetchVehicleEnergyTypesQuery();
    const { data: headUnits = [] } = useFetchMmtHeadUnitsQuery();
    const { data: functionKeys = [] } = useFetchFunctionKeysQuery();

    const energyTypeOptions = energyTypes.map(type => ({ value: type, label: type, id: type }));
    const headUnitOptions = headUnits.map(unit => ({ value: unit, label: unit, id: unit }));
    const functionKeyOptions = functionKeys.map(key => ({ value: key, label: key, id: key }));
    return (
        <div>
            <h4><FormattedMessage id="intl-msg:software" defaultMessage="Software" /></h4>
            <hr />

            <div id={FILTER_IDS.ESSENTIAL_IMAGE}>
                <HeartWrapper>
                    <Controller
                        name='lastEssentialImageReportedVersion'
                        control={control}
                        render={({ field: { value, onChange } }) => (
                            <EssentialImageFormItem
                                value={value}
                                onChange={onChange}
                            />
                        )}
                    />
                </HeartWrapper>
            </div>
            <div id={FILTER_IDS.DISTRO_VERSION}>
                <HeartWrapper>
                    <Controller
                        name='distroVersion'
                        control={control}
                        render={({ field: { value, onChange } }) => (
                            <DistroFormItem
                                value={value}
                                onChange={onChange}
                            />
                        )}
                    />
                </HeartWrapper>
            </div>
            <div id={FILTER_IDS.SW_VERSION}>
                <HeartWrapper>
                    <Controller
                        name='swVersion'
                        control={control}
                        render={({ field: { value: swVersionValue } }) => (
                            <Controller
                                name='swVersionRange'
                                control={control}
                                render={({ field: { value: swVersionRangeValue } }) => (
                                    <BaseSwFormItem
                                        swVersion={swVersionValue}
                                        swVersionRange={swVersionRangeValue}
                                        onSwVersionChange={(val) => {
                                            setValue('swVersion', val);
                                            setValue('swVersionRange', undefined);
                                        }}
                                        onSwVersionRangeChange={(val) => {
                                            setValue('swVersionRange', val);
                                            setValue('swVersion', undefined);
                                        }}
                                    />
                                )}
                            />
                        )}
                    />
                </HeartWrapper>
            </div>
            <div id={FILTER_IDS.INSTALLED_DELIVERABLES}>
                <HeartWrapper>
                    <Controller
                        name='installedDeliverables'
                        control={control}
                        render={({ field: { value, onChange } }) => (
                            <InstalledDeliverablesFormGroup
                                values={value}
                                onChange={onChange}
                                noCompatibilitySeparator={false}
                            />
                        )}
                    />
                </HeartWrapper>
            </div>

            <div id={FILTER_IDS.HAS_MMT_PROPERTIES}>
                <HeartWrapper>
                    <Controller
                        name='hasMmtProperties'
                        control={control}
                        defaultValue='all'
                        render={({ field: { value, onChange } }) => (
                            <RadioButtonListOTA
                                labelId='intl-msg:hasMmtProperties'
                                options={mmtPropertiesOptions}
                                value={value}
                                onChange={onChange}
                            />
                        )}
                    />
                </HeartWrapper>
            </div>

            <div id={FILTER_IDS.VEHICLE_ENERGY_TYPE} className='form-group'>
                <HeartWrapper>
                    <Controller
                        name='vehicleEnergyTypes'
                        control={control}
                        render={({ field: { value, onChange } }) => (
                            <MultiSelectOTA
                                label='intl-msg:vehicleEnergyType'
                                options={energyTypeOptions}
                                value={value}
                                onChange={onChange}
                            />
                        )}
                    />
                </HeartWrapper>
            </div>

            <div id={FILTER_IDS.MMT_HEAD_UNIT} className='form-group'>
                <HeartWrapper>
                    <Controller
                        name='mmtHeadUnits'
                        control={control}
                        render={({ field: { value, onChange } }) => (
                            <MultiSelectOTA
                                label='intl-msg:mmtHeadUnit'
                                options={headUnitOptions}
                                value={value}
                                onChange={onChange}
                            />
                        )}
                    />
                </HeartWrapper>
            </div>

            <div id={FILTER_IDS.MMT_FUNCTIONS} className='form-group'>
                <HeartWrapper>
                    <Controller
                        name='mmtFunctions'
                        control={control}
                        render={({ field: { value, onChange } }) => (
                            <MultiSelectOTA
                                label='intl-msg:mmtFunctions'
                                options={functionKeyOptions}
                                value={value}
                                onChange={onChange}
                            />
                        )}
                    />
                </HeartWrapper>
            </div>
        </div>
    );
};

export default SoftwareFilterSection;
